import * as React from 'react'
import { ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useTimePicker } from './TimePickerContext'
import { minutesToTime, timeToMinutes } from '@/lib/time-utils'

// Generate time options in 15-minute intervals
const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const minutes = i * 15
  return {
    value: minutes,
    label: minutesToTime(minutes)
  }
})

interface TimePickerComboboxProps {
  value: number // minutes from midnight
  onChange: (value: number) => void
  className?: string
  otherTimeSlots?: { start: number; end: number }[]
  isStartTime?: boolean // to know if we're picking start or end time
}

let nextId = 0

export function TimePickerCombobox({
  value,
  onChange,
  className,
  otherTimeSlots = [],
  isStartTime = true
}: TimePickerComboboxProps) {
  const checkOverlap = (timeValue: number) => {
    // If there are no other time slots, nothing is blocked
    if (!otherTimeSlots.length) return false;
    
    return otherTimeSlots.some(slot => {
      if (isStartTime) {
        // For start time: block if the time is within any other slot's range
        return timeValue > slot.start && timeValue < slot.end;
      } else {
        // For end time: block if the time would create an invalid range
        // (i.e., if it would end during another slot's range)
        return timeValue > slot.start && timeValue < slot.end;
      }
    });
  };

  const isTimeDisabled = (timeValue: number) => {
    return checkOverlap(timeValue);
  };

  const handleOverlap = (newValue: number) => {
    if (!checkOverlap(newValue)) {
      onChange(newValue)
    }
  };
  const { openPickerId, setOpenPickerId } = useTimePicker()
  const [id] = React.useState(() => `time-picker-${nextId++}`)
  const selectedRef = React.useRef<HTMLButtonElement>(null)
  const listRef = React.useRef<HTMLDivElement>(null)
  const rootRef = React.useRef<HTMLDivElement>(null)
  const open = openPickerId === id

  React.useEffect(() => {
    if (open && selectedRef.current && listRef.current) {
      selectedRef.current.scrollIntoView({ block: 'center' })
    }
  }, [open])

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpenPickerId(null)
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, setOpenPickerId])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value
    // Check if input matches time format (HH:mm)
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
    if (timeRegex.test(input)) {
      const minutes = timeToMinutes(input)
      handleOverlap(minutes)
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <input
        type="text"
        value={minutesToTime(value)}
        onChange={handleInputChange}
        onClick={() => setOpenPickerId(id)}
        className={cn(
          "w-24 px-3 py-2 text-sm rounded-md border border-input bg-background ring-offset-background",
          className
        )}
      />
      <Button
        variant="ghost"
        size="icon"
        className="h-full aspect-square absolute right-0 top-0"
        onClick={() => setOpenPickerId(open ? null : id)}
      >
        <ChevronsUpDown className="h-4 w-4" />
      </Button>
      {open && (
        <div 
          ref={listRef}
          className="absolute z-50 top-[calc(100%+4px)] left-0 w-48 max-h-60 overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md"
        >
          {TIME_OPTIONS.map((time) => (
            <button
              key={time.value}
              ref={time.value === value ? selectedRef : null}
              className={cn(
                "relative w-full flex items-center py-1.5 px-2 text-sm outline-none",
                value === time.value && "bg-accent text-accent-foreground",
                isTimeDisabled(time.value)
                  ? "opacity-50 cursor-not-allowed text-muted-foreground"
                  : "hover:bg-accent hover:text-accent-foreground cursor-pointer"
              )}
              onClick={() => {
                if (!isTimeDisabled(time.value)) {
                  handleOverlap(time.value)
                  setOpenPickerId(null)
                }
              }}
              disabled={isTimeDisabled(time.value)}
            >
              {time.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
